import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  businessFormDefaults,
  businessFormSchema,
  type BusinessFormValues,
} from './businessFormSchema';

interface BusinessFormProps {
  defaultValues?: BusinessFormValues;
  onSubmit: SubmitHandler<BusinessFormValues>;
  onCancel?: () => void;
  loading?: boolean;
  submitLabel?: string;
  /** When false, hides the submit row (read-only profile view). */
  showSubmit?: boolean;
  /**
   * Optional logo block rendered at the top of the Identity section.
   * The create flows pass a `StagedImagePicker` here so the user can
   * pick a logo before the org exists; the parent stages the file in
   * its own state and uploads after `createOrganization` returns.
   * Logo state is intentionally NOT part of the form schema — the
   * upload is a separate multipart mutation.
   */
  logoSlot?: React.ReactNode;
}

export function BusinessForm({
  defaultValues,
  onSubmit,
  onCancel,
  loading,
  submitLabel = 'Save business',
  showSubmit = true,
  logoSlot,
}: BusinessFormProps) {
  const form = useForm<BusinessFormValues>({
    resolver: zodResolver(businessFormSchema),
    defaultValues: defaultValues ?? businessFormDefaults(),
  });

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6" noValidate>
      <Section title="Identity">
        {logoSlot}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label="Legal name"
            htmlFor="name"
            required
            error={form.formState.errors.name?.message}
          >
            <Input autoComplete="organization" {...form.register('name')} />
          </Field>
          <Field
            label="Nickname"
            htmlFor="nickname"
            hint="Shorter label for the switcher."
          >
            <Input {...form.register('nickname')} />
          </Field>
        </div>
        <Field label="Description" htmlFor="description">
          <Textarea rows={2} {...form.register('description')} />
        </Field>
      </Section>

      <Section title="Defaults">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Currency" htmlFor="currency" required>
            <Input maxLength={3} {...form.register('currency')} />
          </Field>
          <Field label="Date format" htmlFor="dateFormat">
            <Input {...form.register('dateFormat')} />
          </Field>
          <Field label="Timezone" htmlFor="timezone">
            <Input {...form.register('timezone')} />
          </Field>
        </div>
        <Field
          label="Invoice number prefix"
          htmlFor="invoicePrefix"
          hint="Used for new invoice numbers, e.g. INV-0001."
        >
          <Input {...form.register('invoicePrefix')} />
        </Field>
      </Section>

      <Section title="Contact">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field
            label="Email"
            htmlFor="email"
            error={form.formState.errors.email?.message}
          >
            <Input type="email" {...form.register('email')} />
          </Field>
          <Field label="Phone" htmlFor="phone">
            <Input type="tel" {...form.register('phone')} />
          </Field>
          <Field label="Website" htmlFor="website">
            <Input type="url" {...form.register('website')} />
          </Field>
        </div>
      </Section>

      <Section title="Address">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Address line 1" htmlFor="addressLine1">
            <Input {...form.register('addressLine1')} />
          </Field>
          <Field label="Address line 2" htmlFor="addressLine2">
            <Input {...form.register('addressLine2')} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="City" htmlFor="city">
            <Input {...form.register('city')} />
          </Field>
          <Field label="State / Region" htmlFor="region">
            <Input {...form.register('region')} />
          </Field>
          <Field label="Postal code" htmlFor="postalCode">
            <Input {...form.register('postalCode')} />
          </Field>
          <Field label="Country" htmlFor="country">
            <Input {...form.register('country')} />
          </Field>
        </div>
      </Section>

      <Section title="Tax & registration">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Tax number" htmlFor="taxNumber">
            <Input {...form.register('taxNumber')} />
          </Field>
          <Field label="Business number" htmlFor="businessNumber">
            <Input {...form.register('businessNumber')} />
          </Field>
          <Field label="Registration number" htmlFor="registrationNumber">
            <Input {...form.register('registrationNumber')} />
          </Field>
        </div>
      </Section>

      {showSubmit ? (
        <div className="flex items-center justify-end gap-2">
          {onCancel ? (
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
          ) : null}
          <Button type="submit" loading={loading}>
            {submitLabel}
          </Button>
        </div>
      ) : null}
    </form>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-4 rounded-xl border border-line bg-card p-5">
      <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}
